import React from "react";
import { fmt } from "../lib/api";

/**
 * Clean top-down orthographic drawing of the flange face.
 *
 *   • Outer circle  — flange OD (also front & back face)
 *   • Raised face   — inner solid circle (Face 1 Ø)
 *   • PCD           — dashed golden ring
 *   • Bolt holes    — small circles at PCD positions
 *   • Bore          — dark centre circle (pipe Ø)
 *
 * Each nominal dimension gets a radial label. When a live measurement is
 * available for a face it prints "(meas 228.6)" next to the nominal so the
 * surveyor can see design vs measured side-by-side without leaving the 3D
 * view. Simple, unambiguous, engineering-drawing style.
 */
export const Flange2D = ({ flange, activeKind = "face1", live }) => {
  if (!flange) {
    return (
      <div className="flex h-full min-h-[280px] items-center justify-center rounded-sm border border-[var(--border)] bg-[#0e131c] text-xs text-[var(--text-mute)]">
        Select a flange
      </div>
    );
  }

  const od = flange.od || 200;
  const rf = flange.rf_dia || od * 0.65;
  const pcd = flange.pcd || od * 0.75;
  const bd = flange.dia_bolt || 15;
  const pipe = flange.pipe_dia || rf * 0.72;
  const nBolt = flange.no_bolt || 4;

  // Normalize OD radius → 1
  const rOut = 1;
  const rRF = rf / od;
  const rPCD = pcd / od;
  const rBolt = bd / od / 2;
  const rPipe = pipe / od;

  const ink = "#e2e8f0";
  const dim = "#94a3b8";
  const bolt = "#f1c94c";
  const active = { face1: "#ff5555", face2: "#ffb000", face3: "#00e5ff", bolt: "#ff55ff" }[activeKind];
  const isActive = (k) => activeKind === k;

  const meas = {
    face1: live?.face1?.diameter_measured_mm,
    face2: live?.face2?.diameter_measured_mm,
    face3: live?.face3?.diameter_measured_mm,
    bolt: live?.bolt?.diameter_measured_mm,
  };

  // Project measured points onto the top-down 2D view.
  //
  // The backend does the correct plane-fit + projection and hands us the
  // 2D coordinates of every shot in the FITTED FLANGE PLANE, expressed in
  // millimetres relative to the fitted circle centre (points_2d). We just
  // rescale so the design OD sits at radius = 1. This is the ONLY way to
  // draw the shots faithfully — projecting raw world Y/Z would squash the
  // circle into an ellipse whenever the flange face isn't parallel to the
  // world YZ plane (which is almost always).
  //
  // Fallback: if the backend hasn't fitted yet (< 3 points), we still show
  // the raw points naively so the user sees dots appearing as they shoot.
  const scale = 1 / (od / 2);  // mm → OD-normalised units (radius=1)
  const projectFromBlock = (block, rawPts) => {
    // Use backend-projected 2D whenever it's available. The backend fills
    // points_2d either from the block's OWN plane/circle fit (>=3 pts) OR
    // from a sibling face's plane (fallback) so bolt shots don't collapse
    // to the origin before the PCD has 3 pts of its own.
    if (block?.points_2d?.length === rawPts.length && rawPts.length > 0) {
      // Mirror both axes: -u so the drawing matches the OPERATOR's view
      // (not the TS12's back-of-flange view); -v because SVG +y is DOWN
      // while the plane's +v is world-up.
      return block.points_2d.map(([u, v], i) => ({
        x: -u * scale,
        y: -v * scale,
        seq: i + 1,
        residual: block.residuals_mm?.[i] ?? null,
      }));
    }
    // Fallback naive projection — only used when NO face has been fitted
    // yet (so there is no plane to borrow). Centre on cluster centroid.
    if (!rawPts.length) return [];
    const cy = rawPts.reduce((s, p) => s + p.y, 0) / rawPts.length;
    const cz = rawPts.reduce((s, p) => s + p.z, 0) / rawPts.length;
    const mmScale = 1000 * scale;
    return rawPts.map((p, i) => ({
      x: -(p.y - cy) * mmScale,
      y: -(p.z - cz) * mmScale,
      seq: i + 1,
      residual: null,
    }));
  };
  const pickPts = (kind, block) =>
    projectFromBlock(block, (live?.points || []).filter((p) => p.kind === kind));
  const pts = {
    face1: pickPts("face1", live?.face1),
    face2: pickPts("face2", live?.face2),
    face3: pickPts("face3", live?.face3),
    bolt:  pickPts("bolt",  live?.bolt),
  };

  const rowLabel = (design, m) =>
    m == null
      ? `Ø ${fmt(design, 1)} mm`
      : `Ø ${fmt(design, 1)} → ${fmt(m, 1)} · Δ${(m - design >= 0 ? "+" : "") + fmt(m - design, 2)}`;

  return (
    <div className="relative flex h-full min-h-[280px] w-full flex-col rounded-sm border border-[var(--border)] bg-[#0e131c]" data-testid="flange-2d">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-1.5 text-[10px] uppercase tracking-widest">
        <span className="font-semibold text-[var(--text)]">
          {flange.size}" · cl.{flange.class} · {flange.gasket || "—"}
        </span>
        <span className="text-[var(--text-dim)]">Top-down view</span>
      </div>

      {/* SVG drawing fills remaining space */}
      <div className="relative min-h-0 flex-1">
        <svg viewBox="-1.55 -1.55 3.1 3.1" className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid meet">
          <defs>
            <radialGradient id="steel" cx="35%" cy="35%">
              <stop offset="0%" stopColor="#2b3546" />
              <stop offset="70%" stopColor="#1a2130" />
              <stop offset="100%" stopColor="#0f1420" />
            </radialGradient>
            <filter id="hi" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="0.02" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* OD disc */}
          <circle
            cx={0} cy={0} r={rOut}
            fill="url(#steel)"
            stroke={isActive("face2") || isActive("face3") ? active : ink}
            strokeWidth={isActive("face2") || isActive("face3") ? 0.022 : 0.014}
            filter={isActive("face2") || isActive("face3") ? "url(#hi)" : undefined}
          />

          {/* Raised face */}
          <circle
            cx={0} cy={0} r={rRF}
            fill="#0f1420"
            stroke={isActive("face1") ? active : "#5a6478"}
            strokeWidth={isActive("face1") ? 0.02 : 0.012}
            filter={isActive("face1") ? "url(#hi)" : undefined}
          />

          {/* Bore */}
          <circle cx={0} cy={0} r={rPipe} fill="#050607" stroke="#5a6478" strokeWidth={0.008} />

          {/* PCD (thin solid ring — the real bolt count comes from the circles below) */}
          <circle
            cx={0} cy={0} r={rPCD}
            fill="none"
            stroke={isActive("bolt") ? active : bolt}
            strokeWidth={isActive("bolt") ? 0.014 : 0.006}
            strokeOpacity={isActive("bolt") ? 1 : 0.55}
            filter={isActive("bolt") ? "url(#hi)" : undefined}
          />

          {/* Bolt holes — one per design bolt, drawn as through-holes on the PCD */}
          {Array.from({ length: nBolt }).map((_, i) => {
            const a = (i * (2 * Math.PI) / nBolt) - Math.PI / 2;
            const cx = Math.cos(a) * rPCD;
            const cy = Math.sin(a) * rPCD;
            const holeR = Math.max(rBolt, 0.028);
            return (
              <g key={i}>
                {/* dark through-hole */}
                <circle cx={cx} cy={cy} r={holeR} fill="#050607"
                        stroke={isActive("bolt") ? active : bolt}
                        strokeWidth={isActive("bolt") ? 0.012 : 0.008} />
                {/* small centre dot so holes read as through-holes even when tiny */}
                <circle cx={cx} cy={cy} r={holeR * 0.35}
                        fill={isActive("bolt") ? active : bolt} opacity={0.9} />
              </g>
            );
          })}

          {/* Centre crosshair */}
          <line x1={-0.08} y1={0} x2={0.08} y2={0} stroke={dim} strokeWidth={0.006} />
          <line x1={0} y1={-0.08} x2={0} y2={0.08} stroke={dim} strokeWidth={0.006} />

          {/* Measured point overlays — each shot drawn at its ACTUAL
              radial position (not snapped to the design ring) with a
              numeric label so you can see which shot is where and how
              far off design each one landed. */}
          {pts.face1.map((p, i) => (
            <g key={`f1-${i}`}>
              <circle cx={p.x} cy={p.y} r={0.03}
                      fill="#ff5555" stroke="#0e131c" strokeWidth={0.008}
                      data-testid={`flange-2d-face1-${i}`} />
              <text x={p.x + 0.05} y={p.y + 0.02} fontSize={0.06}
                    fill="#ff8a8a" fontFamily="monospace">
                {p.seq}
              </text>
            </g>
          ))}
          {pts.face2.map((p, i) => (
            <g key={`f2-${i}`}>
              <circle cx={p.x} cy={p.y} r={0.03}
                      fill="#ffb000" stroke="#0e131c" strokeWidth={0.008}
                      data-testid={`flange-2d-face2-${i}`} />
              <text x={p.x + 0.05} y={p.y + 0.02} fontSize={0.06}
                    fill="#ffb000" fontFamily="monospace">
                {p.seq}
              </text>
            </g>
          ))}
          {pts.face3.map((p, i) => (
            <g key={`f3-${i}`}>
              <circle cx={p.x} cy={p.y} r={0.025}
                      fill="#00e5ff" stroke="#0e131c" strokeWidth={0.008}
                      data-testid={`flange-2d-face3-${i}`} />
              <text x={p.x + 0.05} y={p.y + 0.02} fontSize={0.06}
                    fill="#00e5ff" fontFamily="monospace">
                {p.seq}
              </text>
            </g>
          ))}
          {pts.bolt.map((p, i) => (
            <g key={`bl-${i}`}>
              <circle cx={p.x} cy={p.y} r={0.03}
                      fill="none" stroke="#ff55ff" strokeWidth={0.012}
                      data-testid={`flange-2d-bolt-${i}`} />
              <text x={p.x + 0.05} y={p.y + 0.02} fontSize={0.06}
                    fill="#ff55ff" fontFamily="monospace">
                {p.seq}
              </text>
            </g>
          ))}

          {/* Diameter tick — a single arrow across the OD at 45° */}
          <g stroke={dim} strokeWidth={0.007} fill="none">
            <line x1={-rOut * 0.7071} y1={-rOut * 0.7071} x2={rOut * 0.7071} y2={rOut * 0.7071} strokeDasharray="0.03 0.02" />
          </g>

          {/* Measured-fit ring for the ACTIVE kind — dashed circle drawn
              at the fitted diameter so you can compare measured vs design
              side-by-side. This is what the app is going to save as your
              flange Ø on finalize. */}
          {(() => {
            const activeMeas = meas[activeKind];
            if (activeMeas == null || !od) return null;
            const rMeas = (activeMeas / od);  // relative to the OD scale
            return (
              <circle cx={0} cy={0} r={rMeas} fill="none"
                      stroke={active} strokeWidth={0.012}
                      strokeDasharray="0.05 0.03" opacity={0.9}
                      data-testid="flange-2d-measured-ring" />
            );
          })()}
        </svg>
      </div>

      {/* Legend / dimension table — bottom rail, does NOT overlap the SVG */}
      <div className="grid shrink-0 grid-cols-1 gap-x-3 gap-y-0.5 border-t border-[var(--border)] bg-black/40 px-2 py-1.5 text-[10px] sm:grid-cols-2">
        <Row color={isActive("face2") || isActive("face3") ? "#e2e8f0" : dim}
             label="OD (F2·F3)" value={rowLabel(od, meas.face2 ?? meas.face3)} highlight={isActive("face2") || isActive("face3")} />
        <Row color={isActive("face1") ? "#ff8a8a" : "#c96b6b"}
             label="Raised (F1)" value={rowLabel(rf, meas.face1)} highlight={isActive("face1")} />
        <Row color={isActive("bolt") ? "#f1c94c" : "#a68a2a"}
             label="Bolt PCD"    value={rowLabel(pcd, meas.bolt)} highlight={isActive("bolt")} />
        <Row color={dim} label="Pipe Ø" value={`Ø ${fmt(pipe, 1)} mm`} />
        <Row color={bolt} label="Bolts"  value={`${nBolt} × Ø ${fmt(bd, 1)} mm`} />
      </div>
    </div>
  );
};

const Row = ({ color, label, value, highlight }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="tracking-widest" style={{ color: highlight ? color : "#94a3b8" }}>
      {label}
    </span>
    <span className="readout tabular-nums" style={{ color, fontWeight: highlight ? 700 : 500 }}>
      {value}
    </span>
  </div>
);
